import { useCallback, useEffect, useState } from 'react';
import { fetchOrgTree } from '../services/adminService';
import { describeOrgTreeError, normalizeOrgTree } from '../utils/orgTree';

/**
 * Loads the team structure of one user from GET /rfp/auth/users/{user_id}/org-tree.
 * Mount it with `key={userId}` so another user starts fresh.
 *
 * @param {string} userId  whose tree to open
 * @param {number} [refreshKey]  change it to load the tree again (e.g. after assigning a member)
 * @returns {{ tree: {admins: object[], teamLeads: object[]}|null, status: 'loading'|'loaded'|'error',
 *            error: string, reload: () => void }}
 */
export const useOrgTree = (userId, refreshKey = 0) => {
    const [tree, setTree] = useState(null);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!userId) {
            setStatus('error');
            setError('This user could not be identified.');
            return undefined;
        }

        let cancelled = false;
        setError('');
        // A reload keeps showing the current tree instead of flashing a loader.
        setStatus((current) => (current === 'loaded' ? current : 'loading'));

        fetchOrgTree(userId)
            .then((response) => {
                if (cancelled) return;
                setTree(normalizeOrgTree(response.data));
                setStatus('loaded');
            })
            .catch((requestError) => {
                if (cancelled) return;
                setError(describeOrgTreeError(requestError));
                setStatus('error');
            });

        return () => {
            cancelled = true;
        };
    }, [userId, refreshKey, attempt]);

    const reload = useCallback(() => {
        setStatus('loading');
        setAttempt((value) => value + 1);
    }, []);

    return { tree, status, error, reload };
};
